import { createTheme } from '@mui/material/styles';
import { COLORS, withOpacity } from './colors';

const DISPLAY_FONT = '"Fredoka", "Inter", "Segoe UI", sans-serif';
const BODY_FONT = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const createAppTheme = () => {
  return createTheme({
    palette: {
      primary: {
        main: COLORS.primary.main,
        light: COLORS.primary.light,
        dark: COLORS.primary.dark,
        contrastText: COLORS.primary.contrastText,
      },
      secondary: {
        main: COLORS.secondary.main,
        light: COLORS.secondary.light,
        dark: COLORS.secondary.dark,
        contrastText: COLORS.secondary.contrastText,
      },
      background: {
        default: COLORS.background.default,
        paper: COLORS.background.paper,
      },
      text: {
        primary: COLORS.text.primary,
        secondary: COLORS.text.secondary,
        disabled: COLORS.text.disabled,
      },
      success: COLORS.success,
      error: COLORS.error,
      warning: COLORS.warning,
      info: COLORS.info,
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: BODY_FONT,
      h1: { fontFamily: DISPLAY_FONT, fontWeight: 700, letterSpacing: '-0.025em' },
      h2: { fontFamily: DISPLAY_FONT, fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.015em' },
      h4: { fontFamily: DISPLAY_FONT, fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontFamily: BODY_FONT, fontWeight: 700 },
      h6: { fontFamily: BODY_FONT, fontWeight: 600 },
      subtitle1: { fontWeight: 600, color: COLORS.text.primary },
      subtitle2: { fontWeight: 600, color: COLORS.text.secondary },
      button: { fontFamily: BODY_FONT, fontWeight: 700, textTransform: 'none' },
    },
    components: {
      // 1. BUTTON: Full pill shape with spring lift on hover and tactile press on active
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 700,
            paddingInline: 22,
            paddingBlock: 10,
            textTransform: 'none',
            transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&:active': {
              transform: 'translateY(1px) scale(0.985)',
            },
          },
          containedPrimary: {
            backgroundColor: COLORS.primary.main,
            color: COLORS.primary.contrastText,
            boxShadow: COLORS.shadow?.buttonPrimary || `0 6px 16px ${withOpacity(COLORS.primary.main, 0.28)}`,
            '&:hover': {
              backgroundColor: COLORS.primary.light,
              boxShadow: COLORS.shadow?.buttonHover || `0 10px 24px ${withOpacity(COLORS.primary.main, 0.38)}`,
              transform: 'translateY(-2px)',
            },
          },
          containedSecondary: {
            backgroundColor: COLORS.secondary.main,
            color: COLORS.secondary.contrastText,
            boxShadow: '0 4px 14px rgba(39, 39, 53, 0.25)',
            '&:hover': {
              backgroundColor: COLORS.secondary.light,
              boxShadow: '0 8px 20px rgba(39, 39, 53, 0.35)',
              transform: 'translateY(-2px)',
            },
          },
          outlined: {
            borderWidth: 2,
            borderColor: COLORS.border?.medium || '#d8ded3',
            '&:hover': {
              borderWidth: 2,
              borderColor: COLORS.primary.main,
              backgroundColor: withOpacity(COLORS.primary.main, 0.06),
              transform: 'translateY(-1px)',
            },
          },
          text: {
            borderRadius: 999,
            paddingInline: 16,
            '&:hover': {
              backgroundColor: withOpacity(COLORS.primary.main, 0.08),
            },
          },
        },
      },

      // 2. CARD: Clean 14px subtle rounded rectangle
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${COLORS.border?.light || '#eaece7'}`,
            backgroundColor: COLORS.background.paper,
            boxShadow: COLORS.shadow?.level1 || '0px 2px 8px -2px rgba(39, 39, 53, 0.05)',
            transition: 'transform 0.24s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.24s ease, border-color 0.24s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: COLORS.shadow?.level2 || '0px 10px 24px -4px rgba(39, 39, 53, 0.08)',
              borderColor: withOpacity(COLORS.primary.main, 0.4),
            },
          },
        },
      },

      // 3. PAPER: 14px rounded container
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 14,
          },
          elevation1: {
            boxShadow: COLORS.shadow?.level1 || '0px 2px 8px -2px rgba(39, 39, 53, 0.05)',
          },
          elevation2: {
            boxShadow: COLORS.shadow?.level2 || '0px 10px 24px -4px rgba(39, 39, 53, 0.08)',
          },
          elevation3: {
            boxShadow: COLORS.shadow?.level3 || '0px 20px 40px -8px rgba(39, 39, 53, 0.16)',
          },
        },
      },

      // 4. CHIP: Full Pill & Micro-interaction
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 600,
            transition: 'all 0.18s ease',
            '&:hover': {
              transform: 'scale(1.04)',
            },
          },
        },
      },

      // 5. SLIDER: Expressive M3 Track + Tactile Thumb (for 3D Printing Quote)
      MuiSlider: {
        styleOverrides: {
          root: {
            height: 8,
            borderRadius: 999,
            color: COLORS.primary.main,
          },
          track: {
            height: 8,
            borderRadius: 999,
            border: 'none',
          },
          rail: {
            height: 8,
            borderRadius: 999,
            backgroundColor: COLORS.surface?.containerHigh || '#e5ede2',
            opacity: 1,
          },
          thumb: {
            height: 22,
            width: 22,
            backgroundColor: '#ffffff',
            border: `3px solid ${COLORS.primary.main}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            transition: 'transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.18s ease',
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 8px ${withOpacity(COLORS.primary.main, 0.16)}`,
            },
            '&.Mui-active': {
              transform: 'scale(1.2)',
              boxShadow: `0 0 0 12px ${withOpacity(COLORS.primary.main, 0.24)}`,
            },
          },
          valueLabel: {
            fontFamily: DISPLAY_FONT,
            fontWeight: 600,
            borderRadius: 999,
            backgroundColor: COLORS.secondary.main,
          },
        },
      },

      // 6. TEXT FIELD & OUTLINED INPUT: 14px Pill + Glowing Focus Ring
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: COLORS.surface?.containerLow || '#f7f9f6',
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.border?.medium || '#d8ded3',
              borderWidth: 1.5,
            },
            '&:hover': {
              backgroundColor: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.primary.main,
              },
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              boxShadow: `0 0 0 4px ${withOpacity(COLORS.primary.main, 0.14)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.primary.main,
                borderWidth: 2,
              },
            },
          },
          input: {
            padding: '12px 16px',
            fontSize: '0.95rem',
          },
        },
      },

      // 7. SELECT & MENU
      MuiSelect: {
        styleOverrides: {
          select: {
            borderRadius: 14,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 18,
            boxShadow: COLORS.shadow?.level3 || '0 12px 32px rgba(39, 39, 53, 0.12)',
            border: `1px solid ${COLORS.border?.light || '#eaece7'}`,
            padding: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 4px',
            fontWeight: 500,
            transition: 'all 0.16s ease',
            '&:hover': {
              backgroundColor: withOpacity(COLORS.primary.main, 0.08),
              transform: 'translateX(3px)',
            },
            '&.Mui-selected': {
              backgroundColor: withOpacity(COLORS.primary.main, 0.14),
              fontWeight: 700,
            },
          },
        },
      },

      // 8. TABS: Segmented Pill Tab Bar
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: COLORS.surface?.container || '#eef3ec',
            borderRadius: 999,
            padding: 4,
            minHeight: 44,
          },
          indicator: {
            height: '100%',
            borderRadius: 999,
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 8px rgba(20,21,28,0.12)',
            zIndex: 0,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.9rem',
            minHeight: 36,
            borderRadius: 999,
            zIndex: 1,
            color: COLORS.text.secondary,
            transition: 'color 0.2s ease',
            '&.Mui-selected': {
              color: COLORS.secondary.main,
            },
          },
        },
      },

      // 9. DIALOG
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 28,
            padding: 8,
            boxShadow: COLORS.shadow?.level3 || '0 20px 40px rgba(39, 39, 53, 0.16)',
            border: `1px solid ${COLORS.border?.light || '#eaece7'}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: DISPLAY_FONT,
            fontWeight: 700,
            fontSize: '1.4rem',
            color: COLORS.secondary.main,
          },
        },
      },

      // 10. TOOLTIP & ALERT
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            backgroundColor: COLORS.secondary.main,
            fontSize: '0.78rem',
            fontWeight: 500,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            fontWeight: 500,
          },
        },
      },
    },
  });
};

export default createAppTheme;
